/**
 * SheetsDashboard - Technical Sheets (Autodesk Build-style)
 * Publish sheets for distribution to the field. Sheets are PDFs; list + viewing + add/settings/audit/deleted placeholders.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';
import { usePresence, ActiveViewers } from '../../common/ViewingTemplate';
import PdfMarkupViewer from './PdfMarkupViewer';

const COLUMNS = [
  { key: 'number', label: 'Number' },
  { key: 'version_set', label: 'Version set' },
  { key: 'discipline', label: 'Discipline' },
  { key: 'tag', label: 'Tag' },
  { key: 'created_at', label: 'Created at' },
  { key: 'created_by', label: 'Created by' },
  { key: 'modified_at', label: 'Last time modified' },
  { key: 'modified_by', label: 'Modified by' },
];

const formatDate = (val) => {
  if (!val) return '';
  const d = new Date(val);
  return isNaN(d.getTime()) ? val : d.toLocaleString();
};

const SheetsDashboard = ({ projectId }) => {
  const { instance, accounts } = useMsal();
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ number: '', version_set: '', discipline: '', tag: '' });
  const [addFile, setAddFile] = useState(null);
  const [addDragOver, setAddDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [autoSplit, setAutoSplit] = useState(true);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [splitStep, setSplitStep] = useState('form'); // form | review | processing
  const [tempFileId, setTempFileId] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [analyzeMeta, setAnalyzeMeta] = useState(null);
  const [splitProgress, setSplitProgress] = useState(0);
  const [splitStatusText, setSplitStatusText] = useState('');
  const pollRef = useRef(null);
  const [viewerSheet, setViewerSheet] = useState(null);
  const [viewerPdfUrl, setViewerPdfUrl] = useState(null);
  const [viewerLoading, setViewerLoading] = useState(false);
  const [auditModalOpen, setAuditModalOpen] = useState(false);
  const [auditLog, setAuditLog] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState(null);

  // Live data push support
  const fetchSheetsRef = useRef(null);
  const handleDataChange = useCallback(() => {
    if (fetchSheetsRef.current) fetchSheetsRef.current();
  }, []);

  // When sheet viewer is open, show presence for that sheet; otherwise dashboard presence
  const presenceType = viewerSheet ? 'sheet' : 'technicalSheets';
  const presenceId = viewerSheet ? String(viewerSheet.id) : (projectId ? String(projectId) : 'global');
  const { viewers, isConnected, connectionState, socketId } = usePresence(presenceType, presenceId, true, handleDataChange);

  const socketIdRef = useRef(null);
  useEffect(() => { socketIdRef.current = socketId; }, [socketId]);

  const syncHeaders = useCallback(() => {
    return socketIdRef.current ? { headers: { 'x-socket-id': socketIdRef.current } } : {};
  }, []);

  const fetchSheets = useCallback(async () => {
    if (!projectId) {
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        `/technical-sheets/projects/${projectId}/sheets`
      );
      if (response.data?.success && Array.isArray(response.data.sheets)) {
        setSheets(response.data.sheets);
      } else {
        setSheets([]);
      }
    } catch (err) {
      console.error('Sheets fetch error:', err);
      const msg = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to load sheets';
      setError(msg);
      setSheets([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, instance, accounts]);

  useEffect(() => { fetchSheetsRef.current = fetchSheets; }, [fetchSheets]);

  useEffect(() => {
    fetchSheets();
  }, [fetchSheets]);

  const showComingSoon = (label) => {
    setToast(label);
    setTimeout(() => setToast(null), 1500);
  };

  const fetchProjectAudit = useCallback(async () => {
    if (!projectId) {
      setAuditError('No project selected');
      setAuditLoading(false);
      return;
    }
    setAuditLoading(true);
    setAuditError(null);
    try {
      const res = await authenticatedApiCall(instance, accounts, 'GET', `/technical-sheets/projects/${projectId}/audit`);
      if (res.data?.success && Array.isArray(res.data.audit)) {
        setAuditLog(res.data.audit);
      } else {
        setAuditLog([]);
        if (res.data && !res.data.success) setAuditError(res.data?.message || res.data?.error || 'Could not load audit log');
      }
    } catch (e) {
      console.error('Fetch project audit error:', e);
      const msg = e.response?.data?.error || e.response?.data?.message || e.message || 'Failed to load audit log';
      setAuditError(msg);
      setAuditLog([]);
    } finally {
      setAuditLoading(false);
    }
  }, [projectId, instance, accounts]);

  const openAuditModal = () => {
    setAuditError(null);
    setAuditModalOpen(true);
    fetchProjectAudit();
  };

  const resetAddState = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setAddForm({ number: '', version_set: '', discipline: '', tag: '' });
    setAddFile(null);
    setUploadError(null);
    setAutoSplit(true);
    setReplaceExisting(false);
    setSplitStep('form');
    setTempFileId(null);
    setProposals([]);
    setAnalyzeMeta(null);
    setSplitProgress(0);
    setSplitStatusText('');
  }, []);

  const openAddModal = () => {
    resetAddState();
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    if (tempFileId && splitStep !== 'processing') {
      authenticatedApiCall(
        instance,
        accounts,
        'DELETE',
        `/technical-sheets/projects/${projectId}/sheets/analyze-split/${tempFileId}`
      ).catch(() => {});
    }
    setShowAddModal(false);
    resetAddState();
  };

  const openSheetViewer = useCallback(async (row) => {
    if ((!row?.file_path && !row?.sharepoint_item_id) || !row?.id || !projectId) return;
    setViewerSheet(row);
    setViewerPdfUrl(null);
    setViewerLoading(true);
    try {
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        `/technical-sheets/sheets/${row.id}/file`,
        null,
        { responseType: 'blob' }
      );
      const blob = response.data;
      if (blob && blob.size) {
        const url = URL.createObjectURL(blob);
        setViewerPdfUrl(url);
      }
    } catch (err) {
      console.error('Sheet PDF load error:', err);
      setToast(err.response?.data?.message || 'Failed to load PDF');
      setTimeout(() => setToast(null), 3000);
      setViewerSheet(null);
    } finally {
      setViewerLoading(false);
    }
  }, [projectId, instance, accounts]);

  const closeSheetViewer = useCallback(() => {
    if (viewerPdfUrl) URL.revokeObjectURL(viewerPdfUrl);
    setViewerPdfUrl(null);
    setViewerSheet(null);
  }, [viewerPdfUrl]);

  useEffect(() => {
    return () => {
      if (viewerPdfUrl) URL.revokeObjectURL(viewerPdfUrl);
    };
  }, [viewerPdfUrl]);

  const updateProposalNumber = (index, value) => {
    setProposals((prev) => prev.map((p, i) => (i === index ? { ...p, number: value } : p)));
  };

  const removeProposal = (index) => {
    setProposals((prev) => prev.filter((_, i) => i !== index));
  };

  const pollSplitJob = useCallback((jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    const tick = async () => {
      try {
        const res = await authenticatedApiCall(
          instance,
          accounts,
          'GET',
          `/technical-sheets/projects/${projectId}/sheets/split-job/${jobId}`
        );
        const data = res.data || {};
        setSplitProgress(typeof data.progress === 'number' ? data.progress : 0);
        setSplitStatusText(data.state || 'processing');
        if (data.state === 'completed') {
          if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
          const r = data.result || {};
          const created = r.createdCount ?? r.created?.length ?? 0;
          const skipped = r.skippedCount ?? r.skipped?.length ?? 0;
          const failed = r.failedCount ?? r.failed?.length ?? 0;
          setToast(`Split done: ${created} created, ${skipped} skipped, ${failed} failed`);
          setTimeout(() => setToast(null), 5000);
          setShowAddModal(false);
          resetAddState();
          fetchSheets();
        } else if (data.state === 'failed') {
          if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
          setUploadError(data.error || 'Split job failed');
          setSplitStep('review');
          setUploading(false);
        }
      } catch (err) {
        console.error('Split job poll error:', err);
      }
    };
    tick();
    pollRef.current = setInterval(tick, 2000);
  }, [instance, accounts, projectId, resetAddState, fetchSheets]);

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  const handleAnalyzeSplit = async () => {
    if (!addFile || !projectId) return;
    setUploading(true);
    setUploadError(null);
    setSplitStatusText('Analyzing PDF…');
    try {
      const formData = new FormData();
      formData.append('file', addFile);
      const res = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        `/technical-sheets/projects/${projectId}/sheets/analyze-split`,
        formData,
        { ...syncHeaders(), timeout: 300000 }
      );
      if (!res.data?.success) {
        throw new Error(res.data?.message || 'Analyze failed');
      }
      setTempFileId(res.data.tempFileId);
      setProposals(Array.isArray(res.data.proposals) ? res.data.proposals : []);
      setAnalyzeMeta({
        pageCount: res.data.pageCount,
        proposalCount: res.data.proposalCount,
        trackerIsoCount: res.data.trackerIsoCount,
        originalName: res.data.originalName,
      });
      setSplitStep('review');
    } catch (err) {
      setUploadError(err.response?.data?.message || err.message || 'Analyze failed');
    } finally {
      setUploading(false);
      setSplitStatusText('');
    }
  };

  const handleConfirmSplit = async () => {
    if (!tempFileId || !projectId || !proposals.length) return;
    const items = proposals
      .filter((p) => (p.number || '').trim() && Array.isArray(p.pageIndexes) && p.pageIndexes.length)
      .map((p) => ({
        number: p.number.trim(),
        pageIndexes: p.pageIndexes,
      }));
    if (!items.length) {
      setUploadError('No valid proposals to create. Each row needs a Number.');
      return;
    }
    setUploading(true);
    setUploadError(null);
    setSplitStep('processing');
    setSplitProgress(0);
    setSplitStatusText('Queueing split…');
    try {
      const res = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        `/technical-sheets/projects/${projectId}/sheets/confirm-split`,
        {
          tempFileId,
          items,
          version_set: addForm.version_set || undefined,
          discipline: addForm.discipline || undefined,
          tag: addForm.tag || undefined,
          replaceExisting,
        },
        syncHeaders()
      );
      if (!res.data?.success || !res.data?.jobId) {
        throw new Error(res.data?.message || 'Failed to queue split');
      }
      setSplitStatusText('Processing…');
      pollSplitJob(res.data.jobId);
    } catch (err) {
      setUploadError(err.response?.data?.message || err.message || 'Confirm split failed');
      setSplitStep('review');
      setUploading(false);
    }
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!addFile || !projectId) return;

    if (autoSplit) {
      await handleAnalyzeSplit();
      return;
    }

    setUploading(true);
    setUploadError(null);
    try {
      const formData = new FormData();
      formData.append('file', addFile);
      if (addForm.number) formData.append('number', addForm.number);
      if (addForm.version_set) formData.append('version_set', addForm.version_set);
      if (addForm.discipline) formData.append('discipline', addForm.discipline);
      if (addForm.tag) formData.append('tag', addForm.tag);
      await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        `/technical-sheets/projects/${projectId}/sheets`,
        formData,
        syncHeaders()
      );
      setToast('Sheet added');
      setTimeout(() => setToast(null), 2000);
      closeAddModal();
      fetchSheets();
    } catch (err) {
      setUploadError(err.response?.data?.message || err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const buttonStyle = {
    padding: '8px 16px',
    backgroundColor: 'white',
    color: '#374151',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    transition: 'all 0.15s ease',
  };

  // When viewer is open, show only the viewer (header/sidebar stay visible in layout)
  if (viewerSheet) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, padding: 0 }}>
        {viewerLoading && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f7fa', color: '#6b7280' }}>
            Loading PDF…
          </div>
        )}
        {viewerPdfUrl && !viewerLoading && (
          <PdfMarkupViewer
            pdfUrl={viewerPdfUrl}
            sheetId={viewerSheet.id}
            sheetTitle={[viewerSheet.number, viewerSheet.version_set].filter(Boolean).join(' ') || 'Sheet'}
            onClose={closeSheetViewer}
            projectId={projectId}
            viewers={viewers}
            isConnected={isConnected}
            connectionState={connectionState}
          />
        )}
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        gap: '1rem',
      }}>
        <div>
          <h2 style={{ margin: 0, color: '#333', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            Sheets
          </h2>
          <p style={{ margin: '0.5rem 0 0', color: '#666', fontSize: '0.9rem' }}>
            Publish sheets for distribution to the field. Sheets are published in version sets and are accessible on any device. Add sheets to get started.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Viewing - real-time presence (ActiveViewers shows e.g. "1 viewing") */}
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151' }}>
            <ActiveViewers
              viewers={viewers}
              isConnected={isConnected}
              connectionState={connectionState}
              maxVisible={4}
              showConnectionStatus={false}
            />
          </span>
          <button type="button" onClick={openAddModal} style={buttonStyle} title="Add sheets (drag and drop or import from folder)">
            Add sheets
          </button>
          <button type="button" onClick={() => showComingSoon('Settings')} style={buttonStyle} title="Settings: Collections, Version sets, Disciplines, Permissions, Advanced settings">
            Settings
          </button>
          <button type="button" onClick={openAuditModal} style={buttonStyle} title="Audit log (publish log)">
            Audit log
          </button>
          <button type="button" onClick={() => showComingSoon('Deleted sheets')} style={buttonStyle} title="Deleted sheets">
            Deleted sheets
          </button>
        </div>
      </div>

      {error && (
        <div style={{ padding: '0.75rem 1rem', marginBottom: '1rem', backgroundColor: '#fef2f2', color: '#991b1b', borderRadius: '6px', fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
          <span>{error}</span>
          <button type="button" onClick={() => { setError(null); fetchSheets(); }} style={{ padding: '4px 10px', fontSize: '12px', borderRadius: '4px', border: '1px solid #991b1b', background: 'transparent', color: '#991b1b', cursor: 'pointer' }}>Retry</button>
        </div>
      )}

      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, padding: '12px 20px', backgroundColor: '#374151', color: 'white', borderRadius: '8px', fontSize: '14px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 9999 }}>
          {toast}
        </div>
      )}

      {/* Audit log modal (dashboard) */}
      {auditModalOpen && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={() => setAuditModalOpen(false)}>
          <div style={{ background: '#fff', color: '#374151', borderRadius: '12px', maxWidth: 520, width: '100%', maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 40px rgba(0,0,0,0.15)', border: '1px solid #e5e7eb' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#111827' }}>Audit log — project sheets</h3>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" onClick={fetchProjectAudit} disabled={auditLoading} style={{ padding: '6px 12px', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: auditLoading ? 'wait' : 'pointer', fontSize: '13px' }}>Refresh</button>
                <button type="button" onClick={() => setAuditModalOpen(false)} style={{ padding: '6px 12px', background: '#374151', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>Close</button>
              </div>
            </div>
            <div style={{ overflow: 'auto', padding: '12px', flex: 1 }}>
              {auditError && <p style={{ color: '#dc2626', margin: '0 0 12px 0', fontSize: '13px' }}>{auditError}</p>}
              {auditLoading ? (
                <p style={{ color: '#6b7280', margin: 0 }}>Loading…</p>
              ) : !auditError && auditLog.length === 0 ? (
                <p style={{ color: '#6b7280', margin: 0 }}>No audit entries yet. Add sheets and create markups — entries will appear here.</p>
              ) : !auditError ? (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {auditLog.map((entry) => {
                    const time = entry.created_at ? new Date(entry.created_at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'medium' }) : '—';
                    const user = entry.user_label || 'Unknown';
                    const actionLabel = (entry.action || '').replace(/_/g, ' ');
                    const actionCapitalized = actionLabel ? actionLabel.charAt(0).toUpperCase() + actionLabel.slice(1) : '';
                    let detailsObj = entry.details;
                    if (typeof detailsObj === 'string') {
                      try { detailsObj = JSON.parse(detailsObj); } catch (_) { detailsObj = null; }
                    }
                    const detailsStr = detailsObj && typeof detailsObj === 'object'
                      ? [detailsObj.type, detailsObj.page_number != null ? `page ${detailsObj.page_number}` : ''].filter(Boolean).join(', ') || null
                      : null;
                    const sheetLabel = entry.sheet_number != null ? `Sheet ${entry.sheet_number}` : '';
                    return (
                      <li key={entry.id} style={{ padding: '12px', borderBottom: '1px solid #e5e7eb', fontSize: '13px' }}>
                        <div style={{ color: '#6b7280', marginBottom: 4 }}>{time}{sheetLabel ? ` · ${sheetLabel}` : ''}</div>
                        <div><strong>{user}</strong> — {actionCapitalized}{detailsStr ? ` (${detailsStr})` : ''}</div>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Add Sheet Modal */}
      {showAddModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9998,
          }}
          onClick={(e) => e.target === e.currentTarget && splitStep !== 'processing' && closeAddModal()}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: splitStep === 'review' || splitStep === 'processing' ? '720px' : '480px',
              width: '92%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px', fontSize: '1.25rem' }}>
              {splitStep === 'review' ? 'Review ISO splits' : splitStep === 'processing' ? 'Creating sheets…' : 'Add sheet'}
            </h3>

            {splitStep === 'form' && (
              <>
                <p style={{ margin: '0 0 16px', color: '#6b7280', fontSize: '0.9rem' }}>
                  Upload a PDF. Enable auto-split to detect multiple ISOs, match Progress Tracker names, and create one sheet per drawing.
                </p>
                <form onSubmit={handleAddSubmit}>
                  <div
                    style={{
                      border: `2px dashed ${addDragOver ? '#2563eb' : '#d1d5db'}`,
                      borderRadius: '8px',
                      padding: '24px',
                      textAlign: 'center',
                      marginBottom: '16px',
                      backgroundColor: addDragOver ? '#eff6ff' : '#f9fafb',
                    }}
                    onDragOver={(e) => { e.preventDefault(); setAddDragOver(true); }}
                    onDragLeave={() => setAddDragOver(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setAddDragOver(false);
                      const f = e.dataTransfer?.files?.[0];
                      if (f && (f.type === 'application/pdf' || f.name?.toLowerCase().endsWith('.pdf'))) setAddFile(f);
                    }}
                  >
                    <input
                      type="file"
                      accept=".pdf,application/pdf"
                      onChange={(e) => setAddFile(e.target.files?.[0] || null)}
                      style={{ display: 'none' }}
                      id="sheet-file-input"
                    />
                    <label htmlFor="sheet-file-input" style={{ cursor: 'pointer', display: 'block' }}>
                      {addFile ? addFile.name : 'Drop PDF here or click to choose'}
                    </label>
                  </div>

                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 12, fontSize: '0.9rem', color: '#374151', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={autoSplit}
                      onChange={(e) => setAutoSplit(e.target.checked)}
                      style={{ marginTop: 3 }}
                    />
                    <span>
                      <strong>Auto-split by ISO / match Progress Tracker</strong>
                      <span style={{ display: 'block', color: '#6b7280', fontSize: '0.85rem', marginTop: 2 }}>
                        Reads title-block drawing numbers, names sheets from tracker ISO Sheet # when matched.
                      </span>
                    </span>
                  </label>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
                    {!autoSplit && (
                      <input
                        type="text"
                        placeholder="Number (optional)"
                        value={addForm.number}
                        onChange={(e) => setAddForm((p) => ({ ...p, number: e.target.value }))}
                        style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                      />
                    )}
                    <input
                      type="text"
                      placeholder="Version set (optional)"
                      value={addForm.version_set}
                      onChange={(e) => setAddForm((p) => ({ ...p, version_set: e.target.value }))}
                      style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                    />
                    <input
                      type="text"
                      placeholder="Discipline (optional)"
                      value={addForm.discipline}
                      onChange={(e) => setAddForm((p) => ({ ...p, discipline: e.target.value }))}
                      style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                    />
                    <input
                      type="text"
                      placeholder="Tag (optional)"
                      value={addForm.tag}
                      onChange={(e) => setAddForm((p) => ({ ...p, tag: e.target.value }))}
                      style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                    />
                  </div>
                  {uploadError && (
                    <div style={{ marginBottom: '12px', padding: '8px 12px', backgroundColor: '#fef2f2', color: '#991b1b', borderRadius: '6px', fontSize: '0.9rem' }}>
                      {uploadError}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <button type="button" onClick={closeAddModal} style={{ ...buttonStyle }}>Cancel</button>
                    <button type="submit" disabled={!addFile || uploading} style={{ ...buttonStyle, backgroundColor: '#2563eb', color: 'white', borderColor: '#2563eb', opacity: (!addFile || uploading) ? 0.6 : 1 }}>
                      {uploading ? (autoSplit ? 'Analyzing…' : 'Uploading…') : (autoSplit ? 'Analyze PDF' : 'Add sheet')}
                    </button>
                  </div>
                </form>
              </>
            )}

            {splitStep === 'review' && (
              <>
                {analyzeMeta && (
                  <p style={{ margin: '0 0 12px', color: '#6b7280', fontSize: '0.9rem' }}>
                    {analyzeMeta.originalName} — {analyzeMeta.pageCount} pages → {analyzeMeta.proposalCount} sheet(s)
                    {analyzeMeta.trackerIsoCount != null ? ` · ${analyzeMeta.trackerIsoCount} tracker ISOs available` : ''}
                  </p>
                )}
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, fontSize: '0.85rem', color: '#374151' }}>
                  <input type="checkbox" checked={replaceExisting} onChange={(e) => setReplaceExisting(e.target.checked)} />
                  Replace existing sheets with the same number
                </label>
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', marginBottom: 16, maxHeight: '45vh', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: '#f9fafb', position: 'sticky', top: 0 }}>
                        <th style={{ padding: '8px 10px', textAlign: 'left' }}>Number</th>
                        <th style={{ padding: '8px 10px', textAlign: 'left' }}>Pages</th>
                        <th style={{ padding: '8px 10px', textAlign: 'left' }}>Match</th>
                        <th style={{ padding: '8px 10px', textAlign: 'right' }} />
                      </tr>
                    </thead>
                    <tbody>
                      {proposals.map((p, idx) => (
                        <tr key={`${p.pageRange}-${idx}`} style={{ borderTop: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '6px 10px' }}>
                            <input
                              type="text"
                              value={p.number || ''}
                              onChange={(e) => updateProposalNumber(idx, e.target.value)}
                              style={{ width: '100%', minWidth: 180, padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12 }}
                            />
                            {p.detectedNumber && p.detectedNumber !== p.number && (
                              <div style={{ color: '#9ca3af', fontSize: 11, marginTop: 2 }}>Detected: {p.detectedNumber}</div>
                            )}
                          </td>
                          <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>{p.pageRange || p.pageCount}</td>
                          <td style={{ padding: '6px 10px' }}>
                            {p.matched ? (
                              <span style={{ background: '#d1fae5', color: '#065f46', padding: '2px 6px', borderRadius: 4, fontSize: 11 }}>Tracker</span>
                            ) : (
                              <span style={{ background: '#fef3c7', color: '#92400e', padding: '2px 6px', borderRadius: 4, fontSize: 11 }}>No match</span>
                            )}
                            {p.alreadyExists && (
                              <span style={{ marginLeft: 4, background: '#fee2e2', color: '#991b1b', padding: '2px 6px', borderRadius: 4, fontSize: 11 }}>Exists</span>
                            )}
                          </td>
                          <td style={{ padding: '6px 10px', textAlign: 'right' }}>
                            <button type="button" onClick={() => removeProposal(idx)} style={{ ...buttonStyle, padding: '4px 8px', fontSize: 11 }}>Remove</button>
                          </td>
                        </tr>
                      ))}
                      {proposals.length === 0 && (
                        <tr>
                          <td colSpan={4} style={{ padding: 16, textAlign: 'center', color: '#6b7280' }}>
                            No ISOs detected. Go back and upload as a single sheet, or re-analyze.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {uploadError && (
                  <div style={{ marginBottom: '12px', padding: '8px 12px', backgroundColor: '#fef2f2', color: '#991b1b', borderRadius: '6px', fontSize: '0.9rem' }}>
                    {uploadError}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => {
                      if (tempFileId) {
                        authenticatedApiCall(instance, accounts, 'DELETE', `/technical-sheets/projects/${projectId}/sheets/analyze-split/${tempFileId}`).catch(() => {});
                      }
                      setTempFileId(null);
                      setProposals([]);
                      setAnalyzeMeta(null);
                      setSplitStep('form');
                      setUploadError(null);
                    }}
                    style={buttonStyle}
                  >
                    Back
                  </button>
                  <button type="button" onClick={closeAddModal} style={buttonStyle}>Cancel</button>
                  <button
                    type="button"
                    onClick={handleConfirmSplit}
                    disabled={!proposals.length || uploading}
                    style={{ ...buttonStyle, backgroundColor: '#2563eb', color: 'white', borderColor: '#2563eb', opacity: (!proposals.length || uploading) ? 0.6 : 1 }}
                  >
                    Create {proposals.length} sheet{proposals.length === 1 ? '' : 's'}
                  </button>
                </div>
              </>
            )}

            {splitStep === 'processing' && (
              <div style={{ padding: '12px 0 4px' }}>
                <p style={{ margin: '0 0 12px', color: '#6b7280', fontSize: '0.9rem' }}>
                  {splitStatusText || 'Processing…'} Splitting PDF and uploading to SharePoint.
                </p>
                <div style={{ height: 8, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden', marginBottom: 8 }}>
                  <div style={{ height: '100%', width: `${Math.max(splitProgress, 4)}%`, background: '#2563eb', transition: 'width 0.3s' }} />
                </div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>{Math.round(splitProgress)}%</div>
                {uploadError && (
                  <div style={{ marginTop: 12, padding: '8px 12px', backgroundColor: '#fef2f2', color: '#991b1b', borderRadius: '6px', fontSize: '0.9rem' }}>
                    {uploadError}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Table */}
      <div style={{
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        // Scroll rather than clip: this table is wider than a phone, and with
        // `overflow: hidden` its right-hand columns were unreachable.
        overflowX: 'auto',
        overflowY: 'hidden',
        WebkitOverflowScrolling: 'touch',
        backgroundColor: 'white',
      }}>
        <table style={{ width: '100%', minWidth: 640, borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  style={{
                    padding: '10px 12px',
                    textAlign: 'left',
                    fontWeight: '600',
                    color: '#374151',
                  }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={COLUMNS.length} style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                  Loading sheets...
                </td>
              </tr>
            ) : sheets.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length} style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                  No sheets yet. Use &quot;Add sheets&quot; to upload PDFs.
                </td>
              </tr>
            ) : (
              sheets.map((row) => (
                <tr
                  key={row.id}
                  style={{
                    borderBottom: '1px solid #f3f4f6',
                    cursor: (row.file_path || row.sharepoint_item_id) ? 'pointer' : 'default',
                  }}
                  onClick={() => (row.file_path || row.sharepoint_item_id) && openSheetViewer(row)}
                >
                  {COLUMNS.map((col) => {
                    const val = row[col.key];
                    const display = col.key === 'created_at' || col.key === 'modified_at' ? formatDate(val) : (val ?? '');
                    return (
                      <td key={col.key} style={{ padding: '10px 12px', color: '#111827' }}>
                        {display}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SheetsDashboard;
